<template>
  <cv-form>
    <dl class="field-set type-text title">
      <dt class="field-label">{{ t("media_settings_title") }}</dt>
      <dd class="field-input">
        <text-input
          :value="mediaObjectMetadata.title"
          :mandatory="true"
          placeholder="Untitled Media"
          :disabled="isInputDisabled"
          :character-limit="characterLimit.title"
          :error-message="error.title"
          :text-id="id"
          @input="handleTitleChange"
        />
      </dd>
    </dl>
    <cv-inline-notification
      v-if="showTranscriptInlineNotification"
      kind="info"
      :title="t('media_settings_transcript_page_info_text')"
      low-contrast
      hide-close-button
    />
    <media-component
      ref="media"
      :uuid="uuid"
      type="mo"
      :mediatype="mediaType"
      :show-ai-info-icon="false"
      :hide-controls="false"
      :cue-points="cuePoints"
      :restriction="false"
      :copyright="false"
      :from-media-settings="true"
      :strict-mode="strictMode"
      :is-transcript-enabled="true"
      :transcription-file-version="transcriptionFileVersion"
      :allow-seek-on-paused="true"
      :caption-src="captionSrc"
      :is-from-subpage="true"
      @media-loaded="handleMediaLoaded"
      @media-playable="handleMediaPlayable"
      @media-ready="handleMediaReady"
    />
    <dl class="field-set type-text description">
      <dt class="field-label">{{ t("media_settings_description") }}</dt>
      <dd v-if="showRichTextDescription" class="field-value">
        <rich-text class="description" :content="mediaObjectMetadata.description" />
      </dd>
      <dd v-else class="field-input">
        <text-area
          ref="descriptionInput"
          :value="mediaObjectMetadata.description"
          placeholder="This will be description text"
          :disabled="isInputDisabled"
          :character-limit="characterLimit.description"
          :error-message="error.description"
          :text-id="id"
          @change="handleDescriptionChange"
        />
      </dd>
    </dl>
  </cv-form>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import {
  MediaSettingsEmit,
  useMediaSettingEditView,
} from "../composables/use-media-setting-edit-view.ts";

import TextInput from "@plugins/common/components/TextInput.vue";
import RichText from "@plugins/richtext/components/RichText.vue";
import TextArea from "@plugins/common/components/TextArea.vue";
import MediaComponent from "@plugins/media/components/MediaComponent.vue";
import { CuePoints } from "@plugins/media/types.ts";
import { useTemplateRef, onMounted } from "vue";

const { t } = useI18n();

const emit = defineEmits<MediaSettingsEmit>();

const mediaRef = useTemplateRef<InstanceType<typeof MediaComponent>>("media");

type CharacterLimit = Record<string, number>;
type ErrorBag = Record<string, string>;

const props = withDefaults(
  defineProps<{
    params?: { uuid?: string; mediaType?: string; strictMode?: boolean };
    mediaObjectMetadata?: { title?: string; description?: string };
    characterLimit?: CharacterLimit;
    error?: ErrorBag;
    id?: string;
    showTranscriptInlineNotification?: boolean;
    cuePoints?: CuePoints;
    transcriptionFileVersion?: string;
    isViewingTranscript?: boolean;
    captionSrc?: string;
    processingId?: string;
    strictMode?: boolean;
  }>(),
  {
    params: () => ({}),
    mediaObjectMetadata: () => ({}),
    characterLimit: () => ({}),
    error: () => ({}),
    id: "",
    showTranscriptInlineNotification: true,
    cuePoints: () => ({}) as CuePoints,
    transcriptionFileVersion: "",
    isViewingTranscript: true,
    captionSrc: "",
    processingId: "",
    strictMode: false,
  },
);

const {
  uuid,
  mediaType,
  isInputDisabled,
  showRichTextDescription,
  handleTitleChange,
  handleDescriptionChange,
  handleMediaLoaded,
  handleMediaPlayable,
  handleMediaReady,
} = useMediaSettingEditView(props, emit);

onMounted(() => {
  if (mediaRef.value) {
    emit("mediaRef", mediaRef.value);
  }
});
</script>
